import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ASSETS } from '@swim/config'
import { PacerOrb, useMessages, useNotifications } from '@swim/ui'
import { A11yWidget, useA11y } from '@swim/a11y'
import { ShoppingBag } from 'lucide-react'
import { Footer } from '../layout/Footer'
import { MobileLogoDrawer } from './MobileLogoDrawer'
import { MobileNavDrawer } from './MobileNavDrawer'
import { PacerDrawer } from './PacerDrawer'
import { MenuToggleIcon } from '../chrome/MenuToggleIcon'
import { SearchTrigger } from '../chrome/SearchTrigger'
import { ToteSheet } from '../tote/ToteSheet'
import { useTranslation } from 'react-i18next'
import { useModal } from '@/hooks/use-modal'
import { usePageHeader } from '@/providers/PageHeaderProvider'
import { useSession } from '@/hooks/use-session'
import { useToteStore } from '@/stores/tote'

const AUTH_ROUTES = new Set(['/sign-in', '/sign-up', '/forgot-password', '/reset-password'])

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden
    >
      <circle cx="8.5" cy="8.5" r="5" />
      <line x1="12.5" y1="12.5" x2="17" y2="17" />
    </svg>
  )
}

/**
 * Mobile chrome shell — renders below lg (1024px).
 *
 * ┌──────────────────────────────────┐
 * │ [Logo]        [🔍] [🛍] [☰]    │  ← sticky header h-14
 * ├──────────────────────────────────┤
 * │                                  │
 * │  scrollable content (#main-content)
 * │                                  │
 * │                          [Pacer] │  ← fixed FAB bottom-right z-40
 * └──────────────────────────────────┘
 *
 * Logo tap → MobileLogoDrawer (bottom sheet, workspaces + theme)
 * Search tap → SearchTrigger in controlled mode (command palette dialog)
 * Tote tap → ToteSheet (same component desktop uses, self-managed via useToteStore)
 * Burger tap → MobileNavDrawer (bottom sheet, nav + account)
 * Pacer → same modal as desktop, positioned as FAB
 */
export function MobileLayout() {
  const location = useLocation()
  const isAuthRoute = AUTH_ROUTES.has(location.pathname)
  // Offer detail already renders its own type chip + title + price header in-body
  // (OfferDetailPage.tsx) — showing the generic page title above it duplicates it.
  const isOfferDetail = /\/offers\//.test(location.pathname)

  const { t } = useTranslation('navigation')
  const { t: tCommon } = useTranslation('common')
  const [logoOpen, setLogoOpen] = useState(false)
  const [navOpen, setNavOpen]   = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const pacerModal = useModal()
  const { title: pageTitle } = usePageHeader()
  const { prefs: a11yPrefs } = useA11y()
  const { user } = useSession()
  const { items: toteItems, openTote } = useToteStore()

  const { unreadCount: unreadNotifCount } = useNotifications({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: !!user,
    sseEnabled: false, // SSE not built yet (Web-1kr2)
  })
  const { unreadThreadCount } = useMessages({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: false, // was: enabled: !!user — messages polling killed until the feature is built, see Web-z8o8
  })
  const hasUnread = unreadNotifCount > 0 || unreadThreadCount > 0

  // Close drawers on route change
  useEffect(() => {
    setLogoOpen(false)
    setNavOpen(false)
  }, [location.pathname])

  // Auto-hide header on scroll — standard mobile-app pattern: scrolling down
  // slides the top bar away for max content room; scrolling up, stopping, or
  // being near the top slides it back in. Listens on #main-content because
  // that element (not the window) is the scroll container in this shell.
  const [headerHidden, setHeaderHidden] = useState(false)
  useEffect(() => {
    const el = document.getElementById('main-content')
    if (!el) return
    let lastY = el.scrollTop
    let stopTimer: ReturnType<typeof setTimeout> | undefined
    const onScroll = () => {
      const y = el.scrollTop
      const delta = y - lastY
      lastY = y
      if (y < 56) {
        setHeaderHidden(false) // near top — always show
      } else if (delta > 4) {
        setHeaderHidden(true)
      } else if (delta < -4) {
        setHeaderHidden(false)
      }
      // Reveal shortly after scrolling stops
      if (stopTimer) clearTimeout(stopTimer)
      stopTimer = setTimeout(() => setHeaderHidden(false), 250)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (stopTimer) clearTimeout(stopTimer)
    }
  }, [])

  return (
    <>
      {/* ── Mobile header — fixed overlay, never pushes content ─────────────
           position: fixed takes it out of flex flow entirely so hiding/showing
           via transform never shifts #main-content; the header floats over the
           top of the screen instead. */}
      <header className={`
        fixed inset-x-0 top-0 z-30 h-14
        flex items-center justify-between px-4
        bg-sand-200/90 dark:bg-slate-900/90
        backdrop-blur-sm
        border-b border-sand-300/50 dark:border-slate-700/50
        transition-[opacity,transform] duration-300 ease-out
        ${isAuthRoute ? 'opacity-25 pointer-events-none' : ''}
        ${headerHidden ? '-translate-y-full' : ''}
      `}>

        {/* Logo — opens Logo Drawer */}
        <button
          onClick={() => setLogoOpen(true)}
          aria-label={t('chrome.mobile.logoLabel')}
          className="
            w-11 h-11 flex items-center justify-center
            rounded-xl
            hover:bg-sand-300/60 dark:hover:bg-sand-200/10
            transition-colors
          "
        >
          <img
            src={ASSETS.logos.icon.black.md}
            alt=""
            className="w-8 h-8 dark:hidden"
          />
          <img
            src={ASSETS.logos.icon.white.md}
            alt=""
            className="w-8 h-8 hidden dark:block"
          />
        </button>

        <div className="flex items-center gap-1">
          {/* Search — opens command palette */}
          <button
            onClick={() => setSearchOpen(true)}
            aria-label={t('chrome.mobile.searchLabel')}
            className="
              w-11 h-11 flex items-center justify-center
              rounded-full
              text-slate-500 dark:text-slate-400
              hover:text-slate-700 dark:hover:text-sand-200
              hover:bg-sand-300/60 dark:hover:bg-sand-200/10
              transition-colors
            "
          >
            <SearchIcon />
          </button>

          {/* Tote — opens ToteSheet (self-managed via useToteStore, same component desktop uses) */}
          <button
            type="button"
            onClick={openTote}
            aria-label={`${tCommon('tote.title')}${toteItems.length > 0 ? ` (${toteItems.length})` : ''}`}
            className="
              relative w-11 h-11 flex items-center justify-center
              rounded-full
              text-slate-500 dark:text-slate-400
              hover:text-slate-700 dark:hover:text-sand-200
              hover:bg-sand-300/60 dark:hover:bg-sand-200/10
              transition-colors
            "
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} aria-hidden />
            {toteItems.length > 0 && (
              <span
                aria-hidden
                className="absolute top-1.5 right-1.5 min-w-[14px] h-[14px] rounded-full bg-ocean-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5 leading-none"
              >
                {toteItems.length}
              </span>
            )}
          </button>

          {/* Burger — opens Nav Drawer */}
          <button
            onClick={() => setNavOpen((p) => !p)}
            aria-label={hasUnread ? `${t('chrome.mobile.menuLabel')} — unread` : t('chrome.mobile.menuLabel')}
            aria-expanded={navOpen}
            className="
              relative w-11 h-11 flex items-center justify-center
              rounded-xl
              text-slate-600 dark:text-slate-300
              hover:bg-sand-300/60 dark:hover:bg-sand-200/10
              transition-colors
            "
          >
            <MenuToggleIcon open={navOpen} className="w-6 h-6" />
            {hasUnread && !navOpen && (
              <span
                aria-hidden
                className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-ocean-500 dark:bg-ocean-400 ring-2 ring-sand-200 dark:ring-slate-900"
              />
            )}
          </button>
        </div>
      </header>

      {/* ── Scrollable content ────────────────────────────────────────────── */}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 overflow-y-auto outline-none pt-14"
      >
        {isAuthRoute ? (
          <Outlet />
        ) : (
          <div className="flex flex-col min-h-full">
            {/* Page title — in content body on mobile, scrolls with page */}
            {pageTitle && !isOfferDetail && (
              <div className="px-4 pt-5 pb-1">
                <h1 className="font-display text-2xl font-semibold text-slate-800 dark:text-sand-200">
                  {pageTitle}
                </h1>
              </div>
            )}

            <div className="flex-1">
              <Outlet />
            </div>
            <Footer />
          </div>
        )}
      </main>

      {/* ── Pacer FAB — orb button, fixed bottom-right z-40 ──────────────────
           The PacerDrawer opens on top (z-50 via vaul portal).
           Orb stays visible behind the closed drawer; drawer covers it when open. */}
      {!isAuthRoute && (
        <div className="fixed bottom-6 end-6 z-40">
          <PacerOrb
            isOpen={pacerModal.isOpen}
            onOpen={() => pacerModal.open()}
            onClose={() => pacerModal.close()}
            asInline
            hidePanel
            reducedMotion={a11yPrefs.motion === 'reduced'}
          />
        </div>
      )}

      {/* ── Drawers — z-50 via Drawer portal, covers Pacer (z-40) ─────────── */}
      <MobileLogoDrawer open={logoOpen} onClose={() => setLogoOpen(false)} />
      <MobileNavDrawer  open={navOpen}  onClose={() => setNavOpen(false)}  />
      <PacerDrawer      open={pacerModal.isOpen} onClose={() => pacerModal.close()} />
      <ToteSheet />

      {/* ── Search dialog (controlled by mobile header search button) ─────── */}
      <SearchTrigger open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Same offset as desktop Layout.tsx -- clears the Pacer FAB (40px tall, 24px from bottom) */}
      <A11yWidget bottomOffset={80} />
    </>
  )
}
